import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { trackSignUp } from "../../lib/analytics";
import AuthFormHeader from "./AuthFormHeader";
import {
  authButtonClass,
  authFooterClass,
  authInputClass,
  authLinkClass,
} from "./authStyles";

const SignupForm = ({ onClose, switchToLogin }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const toastOptions = { duration: 5000 };

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match", toastOptions);
      return;
    }

    setLoading(true);

    try {
      const data = await signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      const identities = data?.user?.identities ?? [];
      const confirmationSent = Boolean(data?.user?.confirmation_sent_at);

      if (identities.length === 0) {
        toast.info(
          "If an account already exists for this email, check your inbox (including spam) or try logging in.",
          toastOptions
        );
        onClose();
        return;
      }

      if (!confirmationSent) {
        toast.info(
          "Account request received. If you don't see a verification email shortly, check spam or try again later.",
          toastOptions
        );
        onClose();
        return;
      }

      trackSignUp("password");
      toast.success(
        "Account created! Please check your email (including spam) to verify your account.",
        toastOptions
      );
      onClose();
    } catch (error) {
      const message = error.message?.toLowerCase() ?? "";
      if (message.includes("rate limit")) {
        toast.error(
          "Too many verification emails sent. Please wait a few minutes and try again, or check your spam folder for an earlier email.",
          toastOptions
        );
      } else {
        toast.error(error.message, toastOptions);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-6 py-8 sm:px-9 sm:py-10">
      <AuthFormHeader
        title="Create Account"
        subtitle="Join JmoLux for curated luxury skincare"
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            className={authInputClass}
            required
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            className={authInputClass}
            required
          />
        </div>
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          className={authInputClass}
          required
        />
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          className={authInputClass}
          required
        />
        <input
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange}
          className={authInputClass}
          required
        />
        <button type="submit" disabled={loading} className={`${authButtonClass} mt-2`}>
          {loading ? "Creating Account..." : "Sign Up"}
        </button>
      </form>
      <p className={authFooterClass}>
        Already have an account?{" "}
        <button type="button" onClick={switchToLogin} className={authLinkClass}>
          Login
        </button>
      </p>
    </div>
  );
};

export default SignupForm;
